import { FunctionComponent, MouseEvent, useState } from "react";

import { EMAIL_LOOKUP_CREDITS, PHONE_LOOKUP_CREDITS } from "@signalist/shared-types";

import {
	faArrowRotateRight,
	faCalendarDays,
	faCheck,
	faCopy,
	faEnvelope,
	faMagnifyingGlass,
	faPencil,
	faPhone,
	faTriangleExclamation,
	faWandMagicSparkles,
} from "@fortawesome/pro-regular-svg-icons";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { Icon } from "@/components";
import { Spinner } from "@/components/Spinner/Spinner";

import { EmailProvenanceLines } from "./EmailProvenanceLines";
import { ContactChannelData } from "./types";

export type ContactChannel = "email" | "phone" | "linkedin" | "booking";

interface ContactChannelTooltipProps {
	channel: ContactChannel;
	data: ContactChannelData;
	onCopy: (value: string) => void;
	onEdit: () => void;
	/** Triggers enrichment — only provided for the email / phone channels. */
	onSearch?: () => void;
}

const HEAD: Record<ContactChannel, { icon: IconDefinition; label: string }> = {
	email: { icon: faEnvelope, label: "Email" },
	phone: { icon: faPhone, label: "Phone" },
	linkedin: { icon: faLinkedin, label: "LinkedIn" },
	booking: { icon: faCalendarDays, label: "Booking link" },
};

const NOUN: Record<"email" | "phone", string> = { email: "email", phone: "phone" };
const creditLabel = (credits: number) => `${credits} ${credits === 1 ? "credit" : "credits"}`;
const CREDITS: Record<"email" | "phone", string> = {
	email: creditLabel(EMAIL_LOOKUP_CREDITS),
	phone: creditLabel(PHONE_LOOKUP_CREDITS),
};

const stop = (e: MouseEvent) => {
	e.preventDefault();
	e.stopPropagation();
};

const channelValue = (channel: ContactChannel, data: ContactChannelData): string | null => {
	if (channel === "email") {
		return data.email;
	}

	if (channel === "phone") {
		return data.phone;
	}

	if (channel === "linkedin") {
		return data.linkedinUrl;
	}

	return data.bookingLink;
};

const SearchButton: FunctionComponent<{
	icon: IconDefinition;
	label: string;
	credits: string;
	onClick: () => void;
}> = ({ icon, label, credits, onClick }) => (
	<button
		className="flex w-full items-center justify-center gap-2 rounded-md bg-blue-action py-2 text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90"
		type="button"
		onClick={(e) => {
			stop(e);
			onClick();
		}}
	>
		<Icon className="h-[14px] w-[14px]" icon={icon} />
		{label}
		<span className="rounded bg-white/25 px-1.5 py-px text-[9px] font-bold">{credits}</span>
	</button>
);

/**
 * Hover content for a single contact channel, styled to match the unified
 * contact-edit mockup: a labelled header, a copy-on-click value row (or the
 * email / phone search states — not searched · searching · could not finish · not found · found),
 * a divider and an "Edit contact info" action that opens the shared modal. Under a stored address
 * the email hover also says where it came from, its last verification and the lead's last email
 * (PREC-4262), each line only when known.
 */
export const ContactChannelTooltip: FunctionComponent<ContactChannelTooltipProps> = ({
	channel,
	data,
	onCopy,
	onEdit,
	onSearch,
}) => {
	const [copied, setCopied] = useState(false);

	const head = HEAD[channel];
	const value = channelValue(channel, data);
	const status =
		channel === "email" ? data.emailStatus : channel === "phone" ? data.phoneStatus : null;
	const stripped = value ? value.replace(/^https?:\/\/(www\.)?/, "") : "";

	const copy = (e: MouseEvent) => {
		stop(e);

		if (!value) {
			return;
		}

		onCopy(value);
		setCopied(true);
		setTimeout(() => setCopied(false), 1000);
	};

	const valueRow = (
		<div
			className={`group/val flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 ${
				copied ? "bg-status-success-light" : "hover:bg-gray-50"
			}`}
			onClick={copy}
		>
			<span
				className={`flex-1 truncate text-[12.5px] ${
					copied ? "font-medium text-status-success" : "text-gray-900"
				}`}
			>
				{channel === "linkedin" || channel === "booking" ? stripped : value}
			</span>
			<Icon
				className={`h-[14px] w-[14px] text-gray-500 transition-opacity ${
					copied ? "text-status-success opacity-100" : "opacity-0 group-hover/val:opacity-100"
				}`}
				icon={copied ? faCheck : faCopy}
			/>
		</div>
	);

	const renderBody = () => {
		if (channel === "linkedin" || channel === "booking") {
			return value ? (
				valueRow
			) : (
				<div className="px-2 py-2 text-[12px] text-gray-500">
					No {head.label.toLowerCase()} found
				</div>
			);
		}

		const noun = NOUN[channel];

		if (status === "SEARCHING") {
			return (
				<div className="flex items-center gap-2 px-2 py-2.5 text-[12.5px] text-gray-700">
					<Spinner color="#4d66f5" />
					Searching for {noun}…
				</div>
			);
		}

		// Bounced addresses are kept (so the user can still see/copy them) but carry an explanation
		// of why they're flagged. BOUNCED_VALID still verifies (likely transient); BOUNCED_INVALID
		// no longer verifies (treat as dead).
		if (channel === "email" && (status === "BOUNCED_VALID" || status === "BOUNCED_INVALID")) {
			const bouncedValid = status === "BOUNCED_VALID";

			return (
				<div className="px-2 py-2">
					<div
						className={`mb-2 flex items-start gap-2 rounded-md p-2 text-[12px] ${
							bouncedValid
								? "bg-status-warning-light text-status-warning"
								: "bg-status-error-light text-status-error"
						}`}
					>
						<Icon className="mt-0.5 h-[14px] w-[14px] shrink-0" icon={faTriangleExclamation} />
						<span>
							{bouncedValid
								? "This email bounced but still verifies as valid — the bounce may be temporary (full mailbox, greylisting). Use with caution."
								: "This email bounced and no longer verifies — treat the address as dead."}
						</span>
					</div>
					{value && valueRow}
				</div>
			);
		}

		// A lookup that could not finish is not "not found": the provider gave no answer, so the
		// address may yet exist. Say so, and keep it searchable (PREC-5035). Checked before the
		// stored value, like the bounced branch: a stale RETRY that still carries an address must
		// show the explanation, not just the copy row.
		if (status === "RETRY") {
			return (
				<div className="px-2 py-2">
					<div className="mb-2 flex items-start gap-2 text-[12.5px] text-gray-700">
						<Icon
							className="mt-0.5 h-[14px] w-[14px] shrink-0 text-status-warning"
							icon={faArrowRotateRight}
						/>
						<div>
							<div>The {noun} lookup could not finish</div>
							<div className="mt-0.5 text-[11px] text-text-meta">
								Nothing was charged. Search again to retry.
							</div>
						</div>
					</div>
					{value && valueRow}
					{onSearch && (
						<SearchButton
							credits={CREDITS[channel]}
							icon={faArrowRotateRight}
							label="Search again"
							onClick={onSearch}
						/>
					)}
				</div>
			);
		}

		if (value) {
			return valueRow;
		}

		if (status === "INVALID") {
			return (
				<div className="px-2 py-2">
					<div className="mb-2 flex items-center gap-2 text-[12.5px] text-gray-700">
						<Icon className="h-[14px] w-[14px] text-gray-500" icon={faMagnifyingGlass} />
						No {noun} found
					</div>
					{onSearch && (
						<SearchButton
							credits={CREDITS[channel]}
							icon={faArrowRotateRight}
							label="Search again"
							onClick={onSearch}
						/>
					)}
				</div>
			);
		}

		return (
			<div className="px-2 py-2">
				<div className="mb-2 flex items-center gap-2 text-[12.5px] text-gray-700">
					<Icon className="h-[14px] w-[14px] text-gray-500" icon={faMagnifyingGlass} />
					No {noun} looked up yet
				</div>
				{onSearch && (
					<SearchButton
						credits={CREDITS[channel]}
						icon={faWandMagicSparkles}
						label={`Find ${noun}`}
						onClick={onSearch}
					/>
				)}
			</div>
		);
	};

	return (
		<div className="w-[228px]">
			<div className="flex items-center gap-1.5 px-2 pb-1 pt-0.5 text-[10px] font-bold uppercase tracking-wide text-text-meta">
				<Icon className="h-[12px] w-[12px]" icon={head.icon} />
				{head.label}
			</div>

			{renderBody()}

			{channel === "email" && value && <EmailProvenanceLines data={data} />}

			<div className="my-1 h-px bg-gray-200" />

			<button
				className="flex w-full items-center justify-center gap-1.5 rounded-md py-1.5 text-[12.5px] font-semibold text-blue-action hover:bg-gray-50"
				type="button"
				onClick={(e) => {
					stop(e);
					onEdit();
				}}
			>
				<Icon className="h-[14px] w-[14px]" icon={faPencil} />
				Edit contact info
			</button>
		</div>
	);
};
